import axios, { AxiosResponse } from 'axios';
import { LoginResponse, AuthUser, AuthProvider, RenewalOutcome } from 'types/Auth';
import { UserDB } from 'types/User';
import { BASE_URL, API_BASE_URL } from 'config/api';
import { decodeJwtExp, decodeJwtIssuedAt, decodeJwtSessionEnd } from 'utils/handoffToken';

// Fallback lifetime when a token carries no readable `exp`. The server's real
// lifetime lives in the token; this is only the degraded path (see
// `expiryFromToken`), deliberately the conservative value the UI used before
// `exp` was read at all.
const FALLBACK_LIFETIME_MS = 60 * 60 * 1000;

// How long before expiry the warning appears — but only once renewal has
// already been tried and failed (`AuthContext`). Thirty minutes was sized for a
// banner nobody could act on; now that it carries an action it should appear
// near the event, not half an hour ahead of it.
const EXPIRY_WARNING_MS = 15 * 60 * 1000;

// Short cross-tab lease, so ten open tabs do not each renew the same session.
// `localStorage` is not atomic and two tabs can still slip through: the cost is
// one redundant `/azure-auth` call, so this is a courtesy, not a mutex.
const RENEW_LEASE_KEY = 'authRenewLock';
const RENEW_LEASE_MS = 10 * 1000;

// Last renewal refusal reason we logged, so a per-minute check does not print a
// per-minute line.
let lastRenewRefusal: string | null = null;

/**
 * When the session really ends, per the token itself.
 *
 * The login response carries only `access_token`/`token_type`, so the UI used
 * to invent `now + 1h` and hope it matched the server. It did not: the backend
 * moved to an eight-hour lifetime and every session was still being discarded
 * on the old guess. The token is a standard JWT and its `exp` claim is the one
 * source of truth, so read it — a lifetime duplicated across two repos breaks
 * silently the moment either side moves.
 */
function expiryFromToken(token: string): Date {
  const exp = decodeJwtExp(token);
  if (exp) return exp;
  console.warn(
    '⚠️ [AUTH] Token carries no readable `exp` claim; falling back to a ' +
    '1 hour assumed lifetime. The session may warn early.',
  );
  return new Date(Date.now() + FALLBACK_LIFETIME_MS);
}

const authClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'accept': 'application/json',
  },
});

// Add request interceptor for logging
authClient.interceptors.request.use((config) => {
  console.log('📤 [AUTH] API Request:', {
    method: config.method?.toUpperCase(),
    url: `${config.baseURL}${config.url}`,
    headers: config.headers,
    data: config.data
  });
  return config;
});

// Add response interceptor for logging
authClient.interceptors.response.use(
  (response) => {
    console.log('📥 [AUTH] API Response:', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('❌ [AUTH] API Error:', {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      data: error.response?.data
    });
    return Promise.reject(error);
  }
);

export class AuthService {
  // Legacy keys for backward compatibility (single user)
  private static readonly TOKEN_KEY = 'authToken';
  private static readonly USER_KEY = 'authUser';
  private static readonly EXPIRES_KEY = 'authExpires';
  
  // New keys for multi-user support
  private static readonly CURRENT_USER_ID_KEY = 'currentUserId'; // Stored in sessionStorage (tab-specific)
  private static readonly USERS_LIST_KEY = 'authUsersList'; // List of all logged-in user IDs

  // Get the current user ID for this tab (from sessionStorage)
  private static getCurrentUserId(): string | null {
    return sessionStorage.getItem(this.CURRENT_USER_ID_KEY);
  }

  // Set the current user ID for this tab (in sessionStorage)
  private static setCurrentUserId(userId: string | null): void {
    if (userId) {
      sessionStorage.setItem(this.CURRENT_USER_ID_KEY, userId);
    } else {
      sessionStorage.removeItem(this.CURRENT_USER_ID_KEY);
    }
  }

  // Get storage keys for a specific user ID
  private static getTokenKey(userId: string): string {
    return `authToken_${userId}`;
  }

  private static getUserKey(userId: string): string {
    return `authUser_${userId}`;
  }

  private static getExpiresKey(userId: string): string {
    return `authExpires_${userId}`;
  }

  // Get list of all logged-in user IDs
  private static getUsersList(): string[] {
    try {
      const listStr = localStorage.getItem(this.USERS_LIST_KEY);
      return listStr ? JSON.parse(listStr) : [];
    } catch {
      return [];
    }
  }

  // Add user ID to the list of logged-in users
  private static addUserToList(userId: string): void {
    const list = this.getUsersList();
    if (!list.includes(userId)) {
      list.push(userId);
      localStorage.setItem(this.USERS_LIST_KEY, JSON.stringify(list));
    }
  }

  // Remove user ID from the list
  private static removeUserFromList(userId: string): void {
    const list = this.getUsersList();
    const filtered = list.filter(id => id !== userId);
    if (filtered.length === 0) {
      localStorage.removeItem(this.USERS_LIST_KEY);
    } else {
      localStorage.setItem(this.USERS_LIST_KEY, JSON.stringify(filtered));
    }
  }

  // Login user and store token
  static async login(username: string, password: string): Promise<AuthUser> {
    try {
      // Create form-encoded data for OAuth2 password flow
      const formData = new URLSearchParams();
      formData.append('grant_type', 'password');
      formData.append('username', username);
      formData.append('password', password);
      formData.append('scope', '');
      formData.append('client_id', 'string');
      formData.append('client_secret', 'string');

      const response: AxiosResponse<LoginResponse> = await authClient.post('/auth/login', formData);
      
      console.log('✅ [AUTH] Login response:', {
        status: response.status,
        statusText: response.statusText,
        data: { 
          access_token: response.data.access_token ? `${response.data.access_token.substring(0, 20)}...` : 'null',
          token_type: response.data.token_type
        }
      });
      
      const { access_token, token_type } = response.data;
      
      const expiresAt = expiryFromToken(access_token);

      // Fetch user profile to get role information
      let userProfile: UserDB | null = null;
      try {
        const profileClient = axios.create({
          baseURL: API_BASE_URL,
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
            'Authorization': `Bearer ${access_token}`,
          },
        });
        
        const profileResponse: AxiosResponse<UserDB> = await profileClient.get('/users/me');
        userProfile = profileResponse.data;
        console.log('✅ [AUTH] User profile fetched:', {
          id: userProfile.id,
          email: userProfile.email,
          role: userProfile.role
        });
        console.log('✅ [AUTH] Full user profile:', JSON.stringify(userProfile, null, 2));
      } catch (profileError) {
        console.warn('⚠️ [AUTH] Could not fetch user profile:', profileError);
        // Continue without profile - user can still log in but won't have role info
      }
      
      const user: AuthUser = {
        username,
        token: access_token,
        tokenType: token_type,
        expiresAt,
        role: userProfile?.role,
        id: userProfile?.id,
        tenant_roles: userProfile?.tenant_roles || null,
        provider: 'password',
      };

      // Store authentication data for this user
      if (user.id) {
        this.storeAuthDataForUser(user.id, user);
        // Set this user as the current user for this tab
        this.setCurrentUserId(user.id);
        // Add to users list
        this.addUserToList(user.id);
      } else {
        // Fallback to legacy storage if no user ID (shouldn't happen normally)
        console.warn('⚠️ [AUTH] No user ID available, using legacy storage');
        this.storeAuthData(user);
      }

      return user;
    } catch (error: any) {
      console.error('❌ [AUTH] Login error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      if (error.response?.status === 401) {
        throw new Error('Invalid username or password');
      }
      throw new Error('Login failed. Please try again.');
    }
  }

  static async handleAccessToken(
    accessToken: string,
    provider: AuthProvider = 'password',
  ): Promise<AuthUser> {

    const expiresAt = expiryFromToken(accessToken);

    
      // Fetch user profile to get role information
      let userProfile: UserDB | null = null;
      try {
        const profileClient = axios.create({
          baseURL: API_BASE_URL,
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        
        const profileResponse: AxiosResponse<UserDB> = await profileClient.get('/users/me');
        userProfile = profileResponse.data;
        console.log('✅ [AUTH] User profile fetched:', {
          id: userProfile.id,
          email: userProfile.email,
          role: userProfile.role
        });
        console.log('✅ [AUTH] Full user profile:', JSON.stringify(userProfile, null, 2));
      } catch (profileError) {
        console.warn('⚠️ [AUTH] Could not fetch user profile:', profileError);
        // Continue without profile - user can still log in but won't have role info
      }
      
      const user: AuthUser = {
        username: userProfile?.email || '',
        token: accessToken,
        tokenType: 'Bearer',
        expiresAt,
        role: userProfile?.role,
        id: userProfile?.id,
        tenant_roles: userProfile?.tenant_roles || null,
        provider,
      };

      // Store authentication data for this user
      if (user.id) {
        this.storeAuthDataForUser(user.id, user);
        // Set this user as the current user for this tab
        this.setCurrentUserId(user.id);
        // Add to users list
        this.addUserToList(user.id);
      } else {
        // Fallback to legacy storage if no user ID (shouldn't happen normally)
        console.warn('⚠️ [AUTH] No user ID available, using legacy storage');
        this.storeAuthData(user);
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth-updated'));
      }

      return user;
  }

  // Logout user and clear stored data.
  //
  // The legacy trio is cleared unconditionally. It used to be cleared only in
  // the no-userId branch, so a normal per-user sign-out left `authToken` /
  // `authUser` / `authExpires` behind forever — a second, stale answer to "who
  // is signed in" that outlived every logout.
  static logout(userId?: string): void {
    const targetUserId = userId || this.getCurrentUserId();

    if (targetUserId) {
      localStorage.removeItem(this.getTokenKey(targetUserId));
      localStorage.removeItem(this.getUserKey(targetUserId));
      localStorage.removeItem(this.getExpiresKey(targetUserId));
      this.removeUserFromList(targetUserId);

      // If this was the current user for this tab, clear sessionStorage
      if (targetUserId === this.getCurrentUserId()) {
        this.setCurrentUserId(null);
      }
    }

    this.clearLegacyAuthData();
  }

  private static clearLegacyAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.EXPIRES_KEY);
  }

  /**
   * Read a stored record without judging it. Returns the record even when it
   * has expired, and reports which key it came from.
   *
   * The expiry checks need this: they must be able to *observe* a past-expiry
   * token. `getCurrentUser()` deliberately cannot show them one.
   */
  private static readRecord(
    userId: string | null,
  ): { user: AuthUser; source: string } | null {
    try {
      const [tokenKey, userKey, expiresKey, source] = userId
        ? [this.getTokenKey(userId), this.getUserKey(userId), this.getExpiresKey(userId), `per-user:${userId}`]
        : [this.TOKEN_KEY, this.USER_KEY, this.EXPIRES_KEY, 'legacy'];

      const token = localStorage.getItem(tokenKey);
      const userStr = localStorage.getItem(userKey);
      const expiresStr = localStorage.getItem(expiresKey);
      if (!token || !userStr || !expiresStr) return null;

      const expiresAt = new Date(expiresStr);
      if (Number.isNaN(expiresAt.getTime())) return null;

      return { user: { ...JSON.parse(userStr), token, expiresAt }, source };
    } catch (error) {
      console.error('Error reading stored auth record:', error);
      return null;
    }
  }

  // Get current user from storage (for this tab).
  //
  // Pure: an expired record yields `null`, but nothing is logged out as a side
  // effect of reading. It used to call `logout()` here, which meant any
  // "is the token expired?" predicate built on this getter could never observe
  // an expired token — the getter had already destroyed the session and
  // returned null before the predicate ran. Eviction is now explicit, via
  // `pruneExpiredUsers()`.
  static getCurrentUser(): AuthUser | null {
    return this.getUserById(this.getCurrentUserId());
  }

  // Get user by ID (for switching users or checking other users). Pass null to
  // read the legacy single-user record. Pure, as above.
  static getUserById(userId: string | null): AuthUser | null {
    const record = this.readRecord(userId);
    if (!record) return null;
    return record.user.expiresAt <= new Date() ? null : record.user;
  }

  /**
   * Evict every stored record whose token has expired.
   *
   * The getters used to do this implicitly, as a side effect of being asked a
   * question. Now that they are pure, something has to sweep — otherwise dead
   * records accumulate in localStorage and `getFirstAvailableUser()` keeps
   * offering them to new tabs. Called at app start and on logout.
   */
  static pruneExpiredUsers(): void {
    const now = new Date();

    for (const userId of this.getUsersList()) {
      const record = this.readRecord(userId);
      if (!record || record.user.expiresAt <= now) {
        localStorage.removeItem(this.getTokenKey(userId));
        localStorage.removeItem(this.getUserKey(userId));
        localStorage.removeItem(this.getExpiresKey(userId));
        this.removeUserFromList(userId);
        if (userId === this.getCurrentUserId()) {
          this.setCurrentUserId(null);
        }
      }
    }

    const legacy = this.readRecord(null);
    if (legacy && legacy.user.expiresAt <= now) {
      this.clearLegacyAuthData();
    }
  }


  // Switch to a different user in this tab
  static switchUser(userId: string): boolean {
    const user = this.getUserById(userId);
    if (user) {
      this.setCurrentUserId(userId);
      return true;
    }
    return false;
  }

  // Get all logged-in users
  static getAllUsers(): AuthUser[] {
    const userIds = this.getUsersList();
    const users: AuthUser[] = [];
    
    for (const userId of userIds) {
      const user = this.getUserById(userId);
      if (user) {
        users.push(user);
      }
    }
    
    return users;
  }

  // Get the first available logged-in user (for auto-selection in new tabs)
  static getFirstAvailableUser(): AuthUser | null {
    const allUsers = this.getAllUsers();
    return allUsers.length > 0 ? allUsers[0] : null;
  }

  /**
   * Resolve the user for this tab, inheriting an existing login on a new tab.
   *
   * The inheritance is deliberate (a second tab should not demand a second
   * sign-in), but it used to apply to the wrong case too: when this tab *had* a
   * `currentUserId` whose record was missing or expired, the lookup returned
   * null and the tab silently adopted whoever happened to sit first in
   * `authUsersList`. Adopting a stranger's near-expiry record produces an
   * immediate logout after load — a fast loop, not a slow one. A tab whose own
   * session is dead now stays signed out.
   */
  static initializeTabUser(): AuthUser | null {
    const boundUserId = this.getCurrentUserId();

    if (boundUserId) {
      const boundUser = this.getUserById(boundUserId);
      if (!boundUser) {
        // This tab's session is over. Do not inherit someone else's.
        this.setCurrentUserId(null);
      }
      return boundUser;
    }

    // Genuinely new tab: inherit an existing login if there is one.
    const availableUser = this.getFirstAvailableUser();
    if (availableUser && availableUser.id) {
      this.switchUser(availableUser.id);
      return availableUser;
    }

    return null;
  }

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    return user !== null;
  }

  // Get auth token for API requests
  static getAuthToken(): string | null {
    const user = this.getCurrentUser();
    return user?.token || null;
  }

  // Store authentication data (legacy - single user)
  private static storeAuthData(user: AuthUser): void {
    localStorage.setItem(this.TOKEN_KEY, user.token);
    localStorage.setItem(this.USER_KEY, JSON.stringify({
      username: user.username,
      tokenType: user.tokenType,
      role: user.role,
      id: user.id,
      tenant_roles: user.tenant_roles,
      provider: user.provider,
    }));
    localStorage.setItem(this.EXPIRES_KEY, user.expiresAt.toISOString());
  }

  // Store authentication data for a specific user (multi-user).
  //
  // Writing a per-user record also drops the legacy trio: two live answers to
  // "who is signed in" is the underlying fault behind the expiry check reading
  // a record that a fresh sign-in never wrote.
  private static storeAuthDataForUser(userId: string, user: AuthUser): void {
    this.clearLegacyAuthData();
    localStorage.setItem(this.getTokenKey(userId), user.token);
    localStorage.setItem(this.getUserKey(userId), JSON.stringify({
      username: user.username,
      tokenType: user.tokenType,
      role: user.role,
      id: user.id,
      tenant_roles: user.tenant_roles,
      provider: user.provider,
    }));
    localStorage.setItem(this.getExpiresKey(userId), user.expiresAt.toISOString());
  }

  /**
   * When this tab's session ends, or null if there is no stored session.
   * Reads the raw record, so it can see a token that has already expired.
   */
  static getSessionExpiry(): Date | null {
    return this.readRecord(this.getCurrentUserId())?.user.expiresAt ?? null;
  }

  // The token is past its expiry. This is the only condition that justifies
  // ending a session from a timer.
  static isTokenExpired(): boolean {
    const expiresAt = this.getSessionExpiry();
    return expiresAt !== null && expiresAt <= new Date();
  }

  /**
   * Is the current token past the halfway point of its own life?
   *
   * The trigger for activity-based renewal. Measured from the token's `iat`
   * rather than a guess, so it follows the backend's lifetime automatically —
   * a one-hour token renews around the 30 minute mark, an eight-hour one around
   * the fourth hour, with no constant to keep in step.
   *
   * Null-safe: a token with no readable `iat` (anything minted before session
   * claims existed) reports false, and renewal falls back to the warning window.
   */
  static isTokenPastHalfLife(): boolean {
    const token = this.readRecord(this.getCurrentUserId())?.user.token;
    if (!token) return false;
    const issuedAt = decodeJwtIssuedAt(token);
    const expiresAt = decodeJwtExp(token);
    if (!issuedAt || !expiresAt) return false;
    return Date.now() >= issuedAt.getTime() + (expiresAt.getTime() - issuedAt.getTime()) / 2;
  }

  /**
   * The absolute end of this session — the point renewal cannot move.
   *
   * Read straight from the token's `session_exp`. The UI deliberately does no
   * arithmetic here and holds no copy of the backend's cap: a lifetime
   * duplicated across two repos breaks silently the moment either side moves,
   * which is the exact bug that made this whole workstream necessary.
   *
   * Null when the token predates the claim — then there is no known cap and the
   * UI behaves as it did before.
   */
  static getAbsoluteSessionEnd(): Date | null {
    const token = this.readRecord(this.getCurrentUserId())?.user.token;
    return token ? decodeJwtSessionEnd(token) : null;
  }

  /**
   * Which exchange minted this tab's session, per the stored record.
   *
   * Reads the raw record, so it still answers after the token has expired —
   * which is exactly when the re-auth dialog needs to know whether to offer a
   * password field or an SSO retry.
   */
  static getSessionProvider(): AuthProvider | null {
    const provider = this.readRecord(this.getCurrentUserId())?.user.provider;
    return provider === 'password' || provider === 'azure' || provider === 'google'
      ? provider
      : null;
  }

  // Take the cross-tab renewal lease. Best-effort (see RENEW_LEASE_KEY).
  private static takeRenewLease(): boolean {
    try {
      const held = Number(localStorage.getItem(RENEW_LEASE_KEY));
      if (held && Date.now() - held < RENEW_LEASE_MS) return false;
      localStorage.setItem(RENEW_LEASE_KEY, String(Date.now()));
      return true;
    } catch {
      // Storage unavailable: renewing without the courtesy lease beats not
      // renewing at all.
      return true;
    }
  }

  private static releaseRenewLease(): void {
    try {
      localStorage.removeItem(RENEW_LEASE_KEY);
    } catch {
      /* nothing to release */
    }
  }

  /**
   * Mint a replacement token for the *current* session, silently.
   *
   * The single seam the whole renewal design turns on: when `POST /auth/refresh`
   * lands, it is tried here first and the rest of the app does not change.
   *
   * Today it can only renew SSO sessions, because `/azure-auth` and
   * `/google-auth` re-mint on every call while `/auth/login` needs a password
   * the UI does not keep.
   *
   * **Renewal is bound to the recorded provider and never falls back to the
   * other one.** Those endpoints mint from whatever EasyAuth cookie the browser
   * holds, so a fallback could hand this tab a *different person's* token while
   * their colleague's half-finished rule sits on screen. A record with no
   * provider (written before this shipped) is treated as unrenewable: a banner
   * is a much smaller harm than a silent identity swap.
   */
  static async renewSession(providerOverride?: AuthProvider | null): Promise<RenewalOutcome> {
    const provider = providerOverride ?? this.getSessionProvider();

    if (!this.takeRenewLease()) {
      console.log('⏭️ [AUTH] Renewal already in flight in another tab; skipping');
      return { status: 'retry' };
    }

    try {
      // `POST /auth/refresh` first, for every provider. It is the only path
      // that carries the *same* session forward: `sid`, `auth_time` and
      // `session_exp` are copied unchanged, so the absolute cap keeps counting.
      // The SSO exchanges mint a brand-new session and reset that cap, which is
      // right when a user signs in and wrong as a silent background renewal.
      const refreshed = await this.refreshSession(provider);
      if (refreshed.status !== 'unavailable') return refreshed;

      return await this.renewViaSso(provider);
    } finally {
      this.releaseRenewLease();
    }
  }

  /**
   * Slide the current session forward.
   *
   * Returns `unavailable` — meaning "try something else" — only for a backend
   * that has no such endpoint, or a token too old to carry session claims.
   * Every other answer is final for this attempt.
   */
  private static async refreshSession(provider: AuthProvider | null): Promise<RenewalOutcome> {
    const token = this.readRecord(this.getCurrentUserId())?.user.token;
    if (!token) return { status: 'unavailable' };

    try {
      const response: AxiosResponse<LoginResponse> = await axios.post(
        `${BASE_URL}/auth/refresh`,
        null,
        { headers: { accept: 'application/json', Authorization: `Bearer ${token}` } },
      );

      const accessToken = response.data?.access_token;
      if (!accessToken) return { status: 'unavailable' };

      // Keep the provider: refresh continues the session it was given, and the
      // dialog's password-vs-SSO choice must not drift because of how the token
      // was last minted.
      await this.handleAccessToken(accessToken, provider ?? 'password');
      lastRenewRefusal = null;
      console.log('🔄 [AUTH] Session refreshed');
      return { status: 'renewed' };
    } catch (error: any) {
      const status_ = error?.response?.status;
      const reason = error?.response?.data?.detail?.reason;

      // No endpoint (older admin server), or a token minted before session
      // claims existed: both mean "this cannot slide", not "this session is
      // over". Fall through to the SSO exchange, which can mint a new one.
      if (status_ === 404 || status_ === 405 || reason === 'session_restart_required') {
        return { status: 'unavailable' };
      }

      if (status_ === 403 || status_ === 401) {
        const refusal = reason ?? (status_ === 401 ? 'token_invalid_or_expired' : 'refused');
        if (lastRenewRefusal !== refusal) {
          lastRenewRefusal = refusal;
          console.warn(`⚠️ [AUTH] Refresh refused: ${refusal}`);
        }
        return { status: 'rejected', reason: refusal };
      }

      // Network blip or a 5xx. Say nothing to the user and try again next tick.
      console.warn('⚠️ [AUTH] Refresh could not be completed; will retry:', error?.message);
      return { status: 'retry' };
    }
  }

  /**
   * Re-run the SSO exchange, which mints a **new** session.
   *
   * Only reachable when refresh said `unavailable`. It deliberately does not
   * run after a refusal, and that is a security property rather than tidiness:
   * `/azure-auth` and `/google-auth` call `write_token()`, which starts a fresh
   * session with `auth_time` of now — and neither exchange checks
   * `is_active`. Falling back here after `revoked` or `user_inactive` would
   * silently resurrect a session an administrator had just killed, and after
   * `session_expired_absolute` it would reset the very cap that had just
   * expired.
   */
  private static async renewViaSso(provider: AuthProvider | null): Promise<RenewalOutcome> {
    if (provider !== 'azure' && provider !== 'google') {
      const refusal = provider ?? 'unknown';
      if (lastRenewRefusal !== refusal) {
        lastRenewRefusal = refusal;
        console.log(`ℹ️ [AUTH] Session cannot be renewed silently (provider: ${refusal})`);
      }
      return { status: 'unavailable' };
    }

    try {
      const service =
        provider === 'azure'
          ? (await import('./AzureAuthService')).default
          : (await import('./GoogleAuthService')).default;

      const renewed = await service.forceRenew();
      if (renewed) {
        lastRenewRefusal = null;
        console.log(`🔄 [AUTH] Session renewed via ${provider}`);
        return { status: 'renewed' };
      }

      if (lastRenewRefusal !== `${provider}:failed`) {
        lastRenewRefusal = `${provider}:failed`;
        console.warn(
          `⚠️ [AUTH] ${provider} renewal did not return a token — the platform ` +
          'session has probably expired; a real sign-in is required',
        );
      }
      return { status: 'unavailable' };
    } catch (error) {
      console.warn('⚠️ [AUTH] Session renewal failed:', error);
      return { status: 'retry' };
    }
  }

  /**
   * End this session on the server, not just in this browser.
   *
   * `POST /auth/logout` used to be a no-op — a stateless JWT cannot be taken
   * back — so the UI never called it. It is real now: the backend records the
   * revocation against the token's `sid` and every later request carrying it is
   * refused. Without this call a "signed out" token stays valid until `exp`,
   * which is the whole reason the backend built revocation.
   *
   * Best-effort and never throws: signing out locally must not depend on the
   * network.
   */
  static async revokeSessionOnServer(): Promise<void> {
    const token = this.readRecord(this.getCurrentUserId())?.user.token;
    if (!token) return;
    try {
      await axios.post(`${BASE_URL}/auth/logout`, null, {
        headers: { accept: 'application/json', Authorization: `Bearer ${token}` },
      });
    } catch (error: any) {
      console.warn('⚠️ [AUTH] Could not revoke the session server-side:', error?.message);
    }
  }

  /**
   * The session ends within the warning window.
   *
   * A warning, never a reason to log out: there is no refresh endpoint, so
   * ending the session early only takes time away from the user that the token
   * would still have covered.
   */
  static isTokenExpiringSoon(): boolean {
    const record = this.readRecord(this.getCurrentUserId());
    if (!record) return false;

    const isSoon = record.user.expiresAt <= new Date(Date.now() + EXPIRY_WARNING_MS);
    if (isSoon) {
      // Which record answered, and what it claims. The reported symptom was a
      // fresh token reading as near-expiry, which is only possible if the
      // record read is not the record login wrote — so name it.
      console.log('⏳ [AUTH] Session nearing expiry:', {
        source: record.source,
        expiresAt: record.user.expiresAt.toISOString(),
        minutesLeft: Math.round((record.user.expiresAt.getTime() - Date.now()) / 60000),
      });
    }
    return isSoon;
  }
}

export default AuthService;
